import { GenericOAuth2 } from '@capacitor-community/generic-oauth2';
import { OAuthConfig, OAuthRefreshConfig } from '@/plugins/OAuth2Config';
import { useAuthStore } from '@/stores/auth';
import axios from 'axios';
import * as Sentry from '@sentry/vue';
import { Preferences } from '@capacitor/preferences';
import { emitter } from '@/main';

let _proactiveRefreshTimer = null;

/**
 * Schedule a background token refresh 5 minutes before tokenExp.
 * On success saveTokenData() re-calls this, keeping the chain alive.
 * On NEEDS_INTERACTIVE: shows a snackbar warning but does NOT redirect.
 * On any other failure: silently discards (reactive 401 path handles it).
 *
 * @param {number} tokenExp - Unix timestamp (seconds) of access token expiry
 */
export function scheduleProactiveTokenRefresh(tokenExp) {
    cancelProactiveTokenRefresh();
    if (!tokenExp || tokenExp <= 0) return;

    const fiveMinutesMs = 5 * 60 * 1000;
    const refreshAtMs = (tokenExp * 1000) - fiveMinutesMs;
    const delayMs = refreshAtMs - Date.now();

    if (delayMs <= 0) {
        // Token is already inside the 5-min refresh window (or expired).
        // If not yet expired, attempt a refresh immediately so we don't silently
        // let the session expire on a page-load where the timer was never armed.
        if (tokenExp * 1000 > Date.now()) {
            // Fire immediately — same callback as the timer would have used.
            // We do NOT await here; boot-time callers don't need to block on it.
            (async () => {
                if (!useAuthStore().getIsLoggedInState) return;
                let storedRefreshToken = await Preferences.get({ key: 'refresh_token' }).then(r => r.value);
                if (!storedRefreshToken) {
                    storedRefreshToken = localStorage.getItem('CapacitorStorage.refresh_token');
                }
                if (!storedRefreshToken) return;
                try {
                    await refreshTokens(storedRefreshToken);
                } catch (err) {
                    if (err?.type === 'NEEDS_INTERACTIVE' || err?.type === 'FAILED') {
                        emitter.emit('snackbar', {
                            snackbarText: 'Your session is about to expire. Please save your work and log in again.',
                            snackbarTimeout: 12000,
                        });
                    }
                }
            })();
        }
        return;
    }

    _proactiveRefreshTimer = setTimeout(async () => {
        _proactiveRefreshTimer = null;
        const store = useAuthStore();
        if (!store.getIsLoggedInState) return;

        let storedRefreshToken = await Preferences.get({ key: 'refresh_token' }).then(r => r.value);
        if (!storedRefreshToken) {
            storedRefreshToken = localStorage.getItem('CapacitorStorage.refresh_token');
        }
        if (!storedRefreshToken) return;

        try {
            await refreshTokens(storedRefreshToken);
        } catch (err) {
            if (err?.type === 'NEEDS_INTERACTIVE' || err?.type === 'FAILED') {
                emitter.emit('snackbar', {
                    snackbarText: 'Your session is about to expire. Please save your work and log in again.',
                    snackbarTimeout: 12000,
                });
            }
        }
    }, delayMs);
}

/**
 * Cancel any pending proactive refresh timer (called on logout).
 */
export function cancelProactiveTokenRefresh() {
    if (_proactiveRefreshTimer !== null) {
        clearTimeout(_proactiveRefreshTimer);
        _proactiveRefreshTimer = null;
    }
}

const INTERACTION_REQUIRED_SIGNALS = [
    'interaction_required',
    'login_required',
    'consent_required',
    '50199',
    'aadsts50199',
    'invalid_grant',
    'err_no_authorization_code',
];

/**
 * Returns true when an error string indicates Azure requires interactive re-auth.
 * Covers error codes 50199 (AADSTS50199) and OAuth2 interaction_required / invalid_grant.
 *
 * @param {unknown} reason
 * @returns {boolean}
 */
export function isInteractionRequired(reason) {
    const str = String(reason ?? '').toLowerCase();
    return INTERACTION_REQUIRED_SIGNALS.some(sig => str.includes(sig));
}

/**
 * Web/PWA fallback: exchange refresh token via direct axios POST.
 * Called when GenericOAuth2.refreshToken throws "Functionality not implemented for PWAs yet".
 */
async function refreshTokensWeb(refreshToken) {
    const store = useAuthStore();
    const config = OAuthRefreshConfig(refreshToken);

    const params = new URLSearchParams();
    params.append('client_id', config.appId);
    params.append('scope', config.scope);
    params.append('refresh_token', refreshToken);
    params.append('grant_type', 'refresh_token');

    let response;
    try {
        response = await axios.post(config.accessTokenEndpoint, params, { timeout: 15000 });
    } catch (err) {
        const azureError = err?.response?.data?.error;
        const azureDesc  = err?.response?.data?.error_description;
        console.warn('AUTH: refresh token rejected by Azure', azureError ?? String(err), azureDesc ?? '');
        // DEVAMS-2096: surface the Azure error in Sentry so we can identify the root cause
        // of mid-shift logouts. The error code (e.g. AADSTS700082, AADSTS50131, invalid_grant)
        // will appear as a breadcrumb on the subsequent captureException in jwtInterceptor.
        Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Azure token refresh rejected',
            level: 'warning',
            data: {
                azure_error: azureError ?? 'unknown',
                azure_error_description: azureDesc ?? '',
            },
        });
        const errorStr = azureError || String(err);
        if (isInteractionRequired(errorStr)) {
            throw { type: 'NEEDS_INTERACTIVE', reason: err };
        }
        throw { type: 'FAILED', reason: err };
    }

    if (response.data?.error && isInteractionRequired(response.data.error)) {
        throw { type: 'NEEDS_INTERACTIVE', reason: response.data };
    }

    const tokenData = {
        access_token: response.data.access_token,
        refresh_token: response.data.refresh_token ?? refreshToken,
    };
    await store.saveTokenData(tokenData);
    store.setLoginStatus(true);
    return tokenData;
}

/**
 * @typedef {{ type: 'FAILED', reason: unknown } | { type: 'FAILED', error: unknown }} AuthFailedError
 * @typedef {{ type: 'NEEDS_INTERACTIVE', reason: unknown }} AuthInteractiveError
 * @typedef {{ type: 'CANCELLED', reason: unknown }} AuthCancelledError
 * @typedef {AuthFailedError | AuthInteractiveError | AuthCancelledError} AuthError
 *
 * Key asymmetry: refreshTokens always uses the `reason` key; silentReAuth always uses the `error` key.
 * Callers unwrapping for Sentry must handle both shapes (see jwtInterceptor.js).
 */

/**
 * Attempt a silent token refresh using a stored refresh token.
 *
 * On success: saves token data and returns { access_token, refresh_token }.
 * On interaction_required / 50199: throws { type: 'NEEDS_INTERACTIVE', reason }.
 * On other failure: throws { type: 'FAILED', reason }.
 *
 * @param {string} refreshToken
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export async function refreshTokens(refreshToken) {
    const store = useAuthStore();

    try {
        const response = await GenericOAuth2.refreshToken(OAuthRefreshConfig(refreshToken));
        const data = {
            access_token: response['access_token'],
            refresh_token: response['refresh_token'],
        };
        await store.saveTokenData(data);
        store.setLoginStatus(true);
        return data;
    } catch (reason) {
        const reasonMessage = reason?.message || String(reason) || 'Unknown error';

        if (reasonMessage === 'Functionality not implemented for PWAs yet') {
            return refreshTokensWeb(refreshToken);
        }
        if (isInteractionRequired(reasonMessage)) {
            throw { type: 'NEEDS_INTERACTIVE', reason };
        }
        throw { type: 'FAILED', reason };
    }
}

/**
 * Attempt a silent re-authentication using the Azure AD browser session (prompt=none).
 * This is tried when a refresh token has expired (24h SPA limit) but the user may still
 * have an active Azure AD session in their browser, making a full login unnecessary.
 *
 * On success: saves token data and returns { access_token, refresh_token }.
 * On interaction_required / login_required / consent_required: throws { type: 'NEEDS_INTERACTIVE' }.
 * On other errors: throws { type: 'FAILED', error }.
 *
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export async function silentReAuth() {
    const store = useAuthStore();

    // Build a silent variant of the standard OAuth2 config by merging prompt=none
    // into additionalParameters. This tells Azure AD to authenticate silently using
    // the existing browser session; if interaction is required it returns login_required
    // or interaction_required instead of showing a UI.
    const baseConfig = OAuthConfig();
    const silentConfig = {
        ...baseConfig,
        additionalParameters: {
            ...(baseConfig.additionalParameters ?? {}),
            prompt: 'none',
        },
    };

    let response;
    try {
        const silentTimeout = new Promise((_, reject) =>
            setTimeout(() => reject({ type: 'FAILED', error: new Error('silentReAuth timed out after 6s') }), 6000)
        );
        // Attach a no-op .catch() before entering the race so that if the timeout
        // leg wins and the authenticate promise later rejects (e.g. the OS closes
        // the popup), there is always a handler — preventing an unhandled rejection
        // warning in the browser / Sentry (DEVAMS-1907 / FRONTEND-96).
        const authenticatePromise = GenericOAuth2.authenticate(silentConfig);
        authenticatePromise.catch(() => {});
        response = await Promise.race([authenticatePromise, silentTimeout]);
    } catch (reason) {
        const reasonMessage = reason?.message || String(reason) || '';
        if (isInteractionRequired(reasonMessage)) {
            throw { type: 'NEEDS_INTERACTIVE', reason };
        }
        throw { type: 'FAILED', error: reason };
    }

    if (response['access_token_response']?.error) {
        const errorCode = response['access_token_response'].error;
        if (isInteractionRequired(errorCode)) {
            throw { type: 'NEEDS_INTERACTIVE', reason: response['access_token_response'] };
        }
        throw { type: 'FAILED', error: response['access_token_response'].error };
    }

    const data = {
        access_token: response['access_token_response']['access_token'],
        refresh_token: response['access_token_response']['refresh_token'],
    };
    await store.saveTokenData(data);
    store.setLoginStatus(true);
    return data;
}

/**
 * Trigger a full interactive OAuth2 login via the native browser sheet or system browser.
 * Used when silent refresh is not possible (interaction_required from Azure).
 *
 * On success: saves token data and returns { access_token, refresh_token }.
 * When user dismisses the sheet: throws { type: 'CANCELLED', reason }.
 * On other error: throws { type: 'FAILED', reason }.
 *
 * @param {Object} [options]
 * @param {function} [options.postLoginHook] - Optional async callback executed after token save (e.g. FCM registration).
 * @returns {Promise<{access_token: string, refresh_token: string}>}
 */
export async function interactiveLogin({ postLoginHook } = {}) {
    const store = useAuthStore();

    let response;
    try {
        response = await GenericOAuth2.authenticate(OAuthConfig());
    } catch (reason) {
        const reasonMessage = reason?.message || String(reason) || '';
        if (reasonMessage.includes('USER_CANCELLED') || reasonMessage.toLowerCase().includes('user cancel')) {
            throw { type: 'CANCELLED', reason };
        }
        if (isInteractionRequired(reasonMessage)) {
            throw { type: 'NEEDS_INTERACTIVE', reason };
        }
        throw { type: 'FAILED', reason };
    }

    if (response['access_token_response']?.error) {
        throw { type: 'FAILED', reason: response['access_token_response'].error };
    }

    const data = {
        access_token: response['access_token_response']['access_token'],
        refresh_token: response['access_token_response']['refresh_token'],
    };
    await store.saveTokenData(data);
    store.setLoginStatus(true);
    if (postLoginHook) await postLoginHook(data);
    return data;
}
